import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";

  const [state, setState] = useState({ loading: true, error: null, user: null });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: "No invite token in the link", user: null });
      return;
    }
    api.get(`/invites/${token}`)
      .then(r => setState({ loading: false, error: null, user: r.data }))
      .catch(e => setState({
        loading: false,
        error: e?.response?.data?.detail || "This invite link is invalid or expired",
        user: null,
      }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setSubmitting(true);
    try {
      await api.post("/invites/accept", { token, password });
      setDone(true);
      toast.success("Password set — you can log in now");
      setTimeout(() => nav(`/login?email=${encodeURIComponent(state.user?.email || "")}`, { replace: true }), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-lg">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-secondary" />
          </div>
          <span className="font-display font-semibold text-lg tracking-tight">Treasurescare</span>
        </Link>

        {state.loading && (
          <div className="text-sm text-muted-foreground">Checking your invite…</div>
        )}

        {!state.loading && state.error && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-rose-600 font-medium">
              <AlertTriangle className="w-5 h-5" /> Invite unavailable
            </div>
            <div className="text-sm text-muted-foreground">{state.error}</div>
            <Link to="/login"><Button variant="outline" className="w-full">Go to login</Button></Link>
          </div>
        )}

        {!state.loading && !state.error && done && (
          <div className="space-y-4 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-600 mx-auto">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <div className="font-semibold">All set!</div>
            <div className="text-sm text-muted-foreground">Redirecting to login…</div>
          </div>
        )}

        {!state.loading && !state.error && !done && state.user && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="overline text-secondary">Welcome</div>
              <h1 className="mt-1 font-display text-3xl font-light">Hi {state.user.name?.split(" ")[0]}.</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Set a password to access your Treasurescare account
                {state.user.role ? ` (${state.user.role.replace("_", " ")})` : ""}
                {state.user.department ? ` · ${state.user.department}` : ""}.
              </p>
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={state.user.email || ""} readOnly className="bg-muted/50" />
            </div>
            <div>
              <Label className="text-xs">New password *</Label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
                autoComplete="new-password"
                minLength={8}
                required
                data-testid="invite-password"
              />
              <div className="text-[10px] text-muted-foreground mt-1">At least 8 characters.</div>
            </div>
            <div>
              <Label className="text-xs">Confirm password *</Label>
              <Input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                data-testid="invite-confirm"
              />
            </div>
            <Button type="submit" disabled={submitting} className="w-full" data-testid="invite-submit">
              {submitting ? "Setting password…" : "Set password & continue"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
